import type { Offer, Product } from '@sense/shared';
import { api } from './api';
import { db, getMeta, setMeta, type LedgerRow } from './db';
import { useSession, type Me, type StoreSettings } from './session';

/**
 * Starter is phone-only, like Sense Fresh: the store's data lives on this phone and, after sign-in,
 * the app makes no backend calls (no uploads, no pulls, no cloud reports). Writes that normally go to
 * the server are saved on the phone and also kept in the outbox, so upgrading to a paid plan can
 * upload everything once (see cloud-upgrade.ts).
 */
export const isLocalOnly = (me: Me | null = useSession.getState().me) => me?.tenant.plan_id === 'starter';
export const useLocalOnly = () => useSession((s) => s.me?.tenant.plan_id === 'starter');

/** Ids for things made on the phone: negative, so they never clash with the server's. */
async function localId(key: 'localProductId' | 'localOfferId'): Promise<number> {
  const n = (await getMeta<number>(key, 0)) - 1;
  await setMeta(key, n);
  return n;
}

export type ProductInput = Omit<Product, 'id' | 'version'> & { id?: number };

/** Create or update products (matched by SKU). Returns the ids in the same order. */
export async function saveProducts(items: ProductInput[]): Promise<Array<{ id: number; sku: string }>> {
  if (!isLocalOnly()) {
    const out: Array<{ id: number; sku: string }> = [];
    for (let i = 0; i < items.length; i += 500) {
      const r = await api<{ ids: Array<{ id: number; sku: string }> }>('/api/products', { method: 'POST', json: items.slice(i, i + 500).map(({ id: _id, ...p }) => p) });
      out.push(...r.ids);
    }
    const { pullCatalog } = await import('./sync');
    await pullCatalog();
    return out;
  }
  const out: Array<{ id: number; sku: string }> = [];
  await db.transaction('rw', [db.products, db.outbox, db.meta], async () => {
    for (const { id: _given, ...p } of items) {
      const existing = await db.products.where('sku').equals(p.sku).first();
      const product: Product = { ...p, id: existing?.id ?? (await localId('localProductId')), version: (existing?.version ?? 0) + 1 };
      await db.products.put(product);
      // One pending upload per product: the latest version wins.
      await db.outbox.where('kind').equals('product').filter((r) => (r.doc as Product).sku === p.sku).delete();
      await db.outbox.add({ kind: 'product', doc: product });
      out.push({ id: product.id, sku: product.sku });
    }
  });
  return out;
}

/** Create or update an offer. */
export async function saveOffer(offer: Omit<Offer, 'id'> & { id?: number }): Promise<void> {
  if (!isLocalOnly()) {
    await api('/api/offers', { method: 'POST', json: offer });
    const { pullCatalog } = await import('./sync');
    await pullCatalog();
    return;
  }
  await db.transaction('rw', [db.offers, db.outbox, db.meta], async () => {
    const full: Offer = { ...offer, id: offer.id ?? (await localId('localOfferId')) } as Offer;
    await db.offers.put(full);
    await db.outbox.where('kind').equals('offer').filter((r) => (r.doc as Offer).id === full.id).delete();
    await db.outbox.add({ kind: 'offer', doc: full });
  });
}

export interface StoreUpdate { name: string; address?: string | null; phone?: string | null; gstin?: string | null; settings: Partial<StoreSettings> & Record<string, unknown> }

/** Shop details and settings: to the server, or kept on the phone (uploaded on upgrade). */
export async function saveStore(update: StoreUpdate): Promise<void> {
  if (!isLocalOnly()) {
    await api('/api/store', { method: 'PUT', json: update });
    await useSession.getState().loadMe();
    return;
  }
  const me = useSession.getState().me!;
  const next: Me = {
    ...me,
    store: {
      ...me.store, name: update.name,
      ...(update.address !== undefined ? { address: update.address } : {}),
      ...(update.phone !== undefined ? { phone: update.phone } : {}),
      ...(update.gstin !== undefined ? { gstin: update.gstin } : {}),
      settings: { ...me.store.settings, ...update.settings } as StoreSettings,
    },
  };
  await setMeta('me', next);
  useSession.setState({ me: next });
  // Keep only the newest store change; it is the whole picture.
  await db.outbox.where('kind').equals('store').delete();
  await db.outbox.add({ kind: 'store', doc: { name: next.store.name, address: next.store.address, phone: next.store.phone, gstin: next.store.gstin, settings: next.store.settings } as unknown as Record<string, unknown> });
}

/** Khata payment or adjustment. Returns the new balance. */
export async function recordLedger(customerId: string, kind: 'payment' | 'adjust', amount: number, method?: string, note?: string): Promise<number> {
  if (!isLocalOnly()) {
    const r = await api<{ balance: number }>(`/api/customers/${customerId}/ledger`, { method: 'POST', json: { kind, amount, method, note } });
    return r.balance;
  }
  let balance = 0;
  await db.transaction('rw', [db.ledger, db.customers, db.outbox], async () => {
    const row: LedgerRow = { customer_id: customerId, ts: Date.now(), kind, amount, method: method ?? null, note: note ?? null };
    row.id = await db.ledger.add(row);
    const c = await db.customers.get(customerId);
    // A payment brings the balance down; an adjustment sets the change the owner typed (signed).
    balance = (c?.balance ?? 0) + (kind === 'payment' ? -amount : amount);
    if (c) await db.customers.put({ ...c, balance });
    await db.outbox.add({ kind: 'ledger', doc: row });
  });
  return balance;
}

export interface LedgerEntry { ts: number; kind: string; amount: number; balance: number; ref: string | null; method: string | null; note: string | null }

/** A customer's khata on this phone: credit bills and returns, payments and adjustments, newest first. */
export async function localLedger(customerId: string): Promise<LedgerEntry[]> {
  const bills = await db.bills.filter((b) => b.customer_id === customerId).toArray();
  const rows: Omit<LedgerEntry, 'balance'>[] = [];
  for (const b of bills) {
    const credit = b.payments.filter((p) => p[0] === 'credit').reduce((a, p) => a + p[1], 0);
    if (credit) rows.push({ ts: b.ts, kind: b.kind === 1 ? 'credit_return' : 'credit_sale', amount: b.kind === 1 ? -credit : credit, ref: b.bill_no, method: null, note: null });
  }
  for (const l of await db.ledger.where('customer_id').equals(customerId).toArray()) {
    rows.push({ ts: l.ts, kind: l.kind, amount: l.kind === 'payment' ? -l.amount : l.amount, ref: null, method: l.method, note: l.note });
  }
  rows.sort((a, b) => a.ts - b.ts);
  // Balance brought onto this phone (e.g. from before Starter) that no entry here explains.
  const now = (await db.customers.get(customerId))?.balance ?? 0;
  const opening = now - rows.reduce((a, r) => a + r.amount, 0);
  if (opening) rows.unshift({ ts: rows[0]?.ts ?? Date.now(), kind: 'opening', amount: opening, ref: null, method: null, note: 'Balance brought forward' });
  let bal = 0;
  return rows.map((r) => ({ ...r, balance: (bal += r.amount) })).reverse();
}
